import {debounce} from 'lodash';
import React, {useMemo, useState} from 'react';
import Hotkeys from 'react-hot-keys';
import {mountToPortal} from '../../../../containers/App/Portal';
import {Styled} from './style';
import {Topbar} from "./components/Topbar";
import {Arrows} from "./components/Arrows";
import {MODAL_IMAGE_TRANSITION_TIME} from "../config";
import {useChangeSlide} from "./hooks/useChangeSlide";
import {useLightboxHotkeys} from "./hooks/useLightboxHotkeys";
import {useCurrentLightboxAttributes} from "./hooks/useCurrentLightboxAttributes";
import {useDispatch, useSelector} from "react-redux";
import {MODAL_FADE_OUT_TIME} from "../../logics/LightboxModal/style";
import LightboxActions from "../../../../redux/Lightbox/actions";
import {LightboxContent} from "../LightboxContent";

const {
    close,
} = LightboxActions;

export function LightboxModal() {
    const dispatch = useDispatch();

    const visible = useSelector(state => state.Lightbox.visible);
    const fileCount = useSelector(state => state.Lightbox.files.length);
    const lightboxType = useSelector(state => state.Lightbox.lightboxType);
    const [isExiting, setIsExiting] = useState(false);

    const onClose = (e, forceClose) => {
        e.stopPropagation();
        // prevent closing modal by clicking on non-mask area (eg: image, button, iframe)
        const isMaskArea = e.currentTarget === e.target || e.target?.classList.contains('lightbox-modal');
        const isNoPreview = e.target?.classList.contains('iframe--no-preview') ||
                            e.target?.classList.contains('main-container');

        if (forceClose || isMaskArea || isNoPreview) {
            // performing fade out animation
            setIsExiting(true);

            // after animations
            setTimeout(() => {
                dispatch(close());
                setIsExiting(false);
            }, MODAL_FADE_OUT_TIME);
        }
    };

    const {
        movingDirection,
        goToPrev,
        goToNext,
    } = useChangeSlide();

    const debouncedGoToPrev = useMemo(
        () => debounce(goToPrev, MODAL_IMAGE_TRANSITION_TIME, {leading: true, trailing: false}), [goToPrev],
    );
    const debouncedGoToNext = useMemo(
        () => debounce(goToNext, MODAL_IMAGE_TRANSITION_TIME, {leading: true, trailing: false}), [goToNext],
    );

    const {
        onKeyDown,
    } = useLightboxHotkeys(debouncedGoToPrev, debouncedGoToNext, onClose, fileCount);

    const debouncedKeyDown = useMemo(
        () => debounce(onKeyDown, MODAL_IMAGE_TRANSITION_TIME, {leading: true, trailing: false}), [onKeyDown],
    );

    const {
        contentCaption,
    } = useCurrentLightboxAttributes(lightboxType);

    const modalClassName = `lightbox-modal-root ${isExiting ? 'exiting' : ''}`;
    const modalMaskClassName = `lightbox-modal-mask ${visible ? 'active' : ''} ${isExiting ? 'exiting' : ''}`;
    const modalWrapClassName = 'lightbox-modal-wrap';
    let modalContentClassName = `lightbox-modal`;
    if (isExiting)
        modalContentClassName += ' exiting';
    if (movingDirection)
        modalContentClassName += ` moving ${movingDirection}`;

    return mountToPortal(
        <Styled className={modalClassName}>
            {
                visible &&
                <Hotkeys
                    keyName="left,right,esc"
                    onKeyDown={debouncedKeyDown}
                >
                    <div
                        className={modalMaskClassName}
                        onClick={onClose}
                    />
                    <div className={modalWrapClassName} onClick={onClose}>
                        <Topbar onClose={onClose} />
                        <div className={`${modalContentClassName} ${isExiting ? 'exiting' : ''}`}>
                            <LightboxContent onClose={onClose} />
                        </div>
                        <Arrows
                            isHidden={fileCount <= 1}
                            goToPrev={debouncedGoToPrev}
                            goToNext={debouncedGoToNext}
                        />
                        <div className="lightbox-modal-footer" tabIndex={-1}>
                            <span className="footer__file-name">
                                {contentCaption}
                            </span>
                        </div>
                    </div>
                </Hotkeys>
            }
        </Styled>,
    );
}
